import { CookieInformationDataSourceEnum } from '@inter-ikea-kompis/enums';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';
import OneWebZipCodeCookieInformationService from '../../services/cookie-information/OneWebZipCodeCookieInformationService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
const SUBSCRIBE_CHECK_INTERVAL = 500;
const CACHES_STORE_NAME = 'zipCodeCookieInformationService';
const CACHE_KEY = {
    zipCode: 'zipCode',
    listeners: 'listeners',
    globalListenerConnected: 'globalListenerConnected',
    globalListenerInterval: 'globalListenerInterval'
};
/**
 * This class is used as a simple interface for zip code cookie information services.
 */
export default class ZipCodeCookieInformationService {
    /**
     * Constructor.
     *
     * @param options Options.
     */
    constructor(options) {
        this.service = null;
        this.dexfConfiguration = new DexfConfiguration(options);
        this.cacheStore = options.cache || new GlobalCacheStore();
        this.cache = this.cacheStore.getStore(CACHES_STORE_NAME);
    }
    /**
     * Fetches and returns zip code.
     *
     * @returns Zip code.
     */
    async getZipCode() {
        const service = await this.getService();
        if (service) {
            return await service.getZipCode();
        }
        return this.cache.get(CACHE_KEY.zipCode) || null;
    }
    /**
     * Sets zip code.
     *
     * @param zipCode Zip code.
     */
    async setZipCode(zipCode) {
        this.publish(zipCode);
        const service = await this.getService();
        if (service) {
            await service.setZipCode(zipCode);
        }
    }
    /**
     * Clears zip code.
     */
    async clearZipCode() {
        this.publish(null);
        const service = await this.getService();
        if (service) {
            await service.clearZipCode();
        }
    }
    /**
     * Subscribes to zip code changes.
     *
     * @param listener Listener.
     */
    subscribe(listener) {
        if (!this.cache.has(CACHE_KEY.listeners)) {
            this.cache.set(CACHE_KEY.listeners, []);
        }
        const listeners = this.cache.get(CACHE_KEY.listeners);
        listeners.push(listener);
        this.connectGlobalListener();
    }
    /**
     * Subscribes to zip code changes.
     *
     * @param listener Listener.
     */
    unsubscribe(listener) {
        const listeners = this.cache.get(CACHE_KEY.listeners) || [];
        const index = listeners.indexOf(listener);
        if (index !== -1) {
            listeners.splice(index, 1);
        }
        if (listeners.length === 0) {
            this.disconnectGlobalListener();
        }
    }
    /**
     * Notifies subscribers.
     *
     * @param newZipCode New zip code.
     */
    publish(newZipCode) {
        const zipCode = this.cache.get(CACHE_KEY.zipCode);
        // Empty string and null are considered the same value.
        if ((newZipCode ?? '') !== (zipCode ?? '')) {
            const listeners = this.cache.get(CACHE_KEY.listeners) || [];
            this.cache.set(CACHE_KEY.zipCode, newZipCode);
            for (const listener of listeners) {
                listener(newZipCode);
            }
        }
    }
    /**
     * Connects global listener.
     */
    async connectGlobalListener() {
        if (this.cache.get(CACHE_KEY.globalListenerConnected)) {
            return;
        }
        this.cache.set(CACHE_KEY.globalListenerConnected, true);
        // Only necessary to use interval if we are using the OneWebZipCodeCookieInformationService.
        if (!(await this.getService())) {
            return;
        }
        const zipCode = await this.getZipCode();
        this.cache.set(CACHE_KEY.zipCode, zipCode);
        // We need to check again as the connected state might have changed while waiting for the zip code.
        if (!this.cache.get(CACHE_KEY.globalListenerConnected)) {
            return;
        }
        this.cache.set(CACHE_KEY.globalListenerInterval, setInterval(async () => {
            const newZipCode = await this.getZipCode();
            this.publish(newZipCode);
        }, SUBSCRIBE_CHECK_INTERVAL));
    }
    /**
     * Disconnects global listener.
     */
    disconnectGlobalListener() {
        if (!this.cache.get(CACHE_KEY.globalListenerConnected)) {
            return;
        }
        this.cache.set(CACHE_KEY.globalListenerConnected, false);
        if (this.cache.get(CACHE_KEY.globalListenerInterval)) {
            clearInterval(this.cache.get(CACHE_KEY.globalListenerInterval));
            this.cache.set(CACHE_KEY.globalListenerInterval, null);
        }
    }
    /**
     * Returns service.
     *
     * @returns Service.
     */
    async getService() {
        if (this.service) {
            return this.service;
        }
        const settings = await this.dexfConfiguration.getSettings();
        if (settings.kompis.serviceSettings.cookieInformationDataSource ===
            CookieInformationDataSourceEnum.disabled) {
            return null;
        }
        this.service = new OneWebZipCodeCookieInformationService({
            endpoint: settings.kompis.endpoints.oneWebCookieInformationService,
            cacheStore: this.cacheStore
        });
        return this.service;
    }
}
//# sourceMappingURL=ZipCodeCookieInformationService.js.map